import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Autocomplete,
} from "@mui/material";
import styled from "styled-components";
import DeleteIcon from "@mui/icons-material/Delete";
import { Backdrop, Field } from "../Auth/styled-component";
import DotLoader from "../../components/Loader/DotLoader";
import { TextField } from "@mui/material";
import {
  createExpenseCategory,
  listExpenseCategories,
  type ExpenseCategory,
} from "../../services/financeApi";
import { getAllInventory } from "../../services/inventoryApi";
import LowStockControlsWeb, {
  LowStockValue,
} from "../../components/LowStockControlsWeb"; // adjust path

const ModalWrapper = styled(Box)`
  max-height: 90vh;
  overflow-y: auto;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  max-width: 900px;
  width: 95%;
  margin: 5vh auto;
  padding: 2rem 2rem 3.5rem;
  display: flex;
  flex-direction: row;
  gap: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const RightPanel = styled.div`
  flex: 2;
`;

const FieldRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const VariantRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const AddVariantButton = styled(Button)`
  margin-top: 1rem;
  background: #dbeafe; /* lighter blue-100 */
  color: #2563eb; /* strong blue-600 */
  &:hover {
    background: #bfdbfe; /* hover lighter blue */
  }
`;

const RemoveButton = styled(IconButton)`
  && {
    background-color: transparent !important;
    transition:
      background-color 0.2s ease,
      color 0.2s ease;

    &:hover {
      color: #dc2626; /* red-600 */
    }

    .MuiTouchRipple-root {
      display: none; /* disables ripple effect */
    }
  }
`;

const LeftPanel = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
`;

const ImageContainer = styled.label<{ isDragging?: boolean }>`
  position: relative;
  width: 100%;
  max-width: 300px;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
  cursor: pointer;
  box-shadow: ${({ isDragging }) =>
    isDragging ? "0 0 0 3px #2563eb" : "0 0 10px rgba(0, 0, 0, 0.1)"};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  transition:
    box-shadow 0.3s ease,
    background-color 0.3s ease;
  background-color: ${({ isDragging }) =>
    isDragging ? "#f0f4ff" : "transparent"};

  &:hover {
    box-shadow: 0 0 0 3px #2563eb;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    opacity: ${({ isDragging }) => (isDragging ? 0.5 : 1)};
  }

  .placeholder-initial {
    width: 100%;
    height: 100%;
    display: grid;
    place-items: center;
    background: linear-gradient(145deg, #eff6ff 0%, #f8fafc 100%);
    color: #2563eb;
    font-size: 96px;
    font-weight: 800;
    text-transform: uppercase;
  }

  span {
    position: absolute;
    bottom: 0;
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
    color: white;
    font-size: 12px;
    padding: 0.4rem 0;
    text-align: center;
    opacity: 0;
    transition: opacity 0.2s ease-in-out;
  }

  &:hover span {
    opacity: 1;
  }
`;
export const MotionBox = styled(Box)`
  transition:
    transform 0.4s ease,
    opacity 0.4s ease;
`;

const HiddenInput = styled.input`
  display: none;
`;

const compactTextFieldSx = {
  "& .MuiOutlinedInput-root": {
    height: "44px",
    fontSize: "0.9rem",
  },
  "& .MuiInputBase-input": {
    padding: "12px 14px",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
    transform: "translate(14px, 14px) scale(1)",
  },
  "& .MuiInputLabel-shrink": {
    transform: "translate(14px, -9px) scale(0.75)",
  },
};
export interface InventoryFormData {
  itemName: string;
  businessName: string;
  brand: string;
  quantity: string;
  costPerUnit: string;
  categoryId: string;
  defaultSellingPrice: string;
  imageUrl: string;
  sku?: string;
  variants: Array<{ attribute: string; value: string }>;
  imageFile?: File;
  notes?: string;
}
interface AddInventoryModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  initialData?: Partial<InventoryFormData>;
}

const AddInventoryModal: React.FC<AddInventoryModalProps> = ({
  open,
  onClose,
  onSave,
  initialData,
}) => {
  const makeSkuPrefix = useCallback((value: string) => {
    const normalized = value
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, "")
      .trim();
    return normalized.slice(0, 3);
  }, []);

  const nextSkuForCategory = useCallback(
    (categoryName: string, items: Array<{ sku?: string }>) => {
      const prefix = makeSkuPrefix(categoryName);
      if (!prefix) return "";
      const highest = items.reduce((max, item) => {
        const rawSku = String(item?.sku || "")
          .trim()
          .toUpperCase();
        const matched = rawSku.match(new RegExp(`^${prefix}-(\\d{3})$`));
        if (!matched?.[1]) return max;
        return Math.max(max, Number(matched[1]));
      }, 0);
      return `${prefix}-${String(highest + 1).padStart(3, "0")}`;
    },
    [makeSkuPrefix],
  );

  const emptyForm = useMemo<InventoryFormData>(
    () => ({
      itemName: "",
      businessName: "",
      brand: "",
      quantity: "",
      costPerUnit: "",
      categoryId: "",
      defaultSellingPrice: "",
      imageUrl: "",
      sku: "",
      imageFile: undefined,
      variants: [],
      notes: "",
    }),
    [],
  );

  const [formData, setFormData] = useState<InventoryFormData>(emptyForm);

  const [isDragging, setIsDragging] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [categories, setCategories] = useState<ExpenseCategory[]>([]);
  const [inventoryItems, setInventoryItems] = useState<
    Array<{ sku?: string; brand?: string }>
  >([]);
  const [categoryInput, setCategoryInput] = useState("");
  const [skuAutoFillLocked, setSkuAutoFillLocked] = useState(false);
  const [autoGeneratedSku, setAutoGeneratedSku] = useState("");
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const emptyLowStock: LowStockValue = {
    enabled: false,
    threshold: "",
    alertOnce: true,
    cooldownHours: "24",
    channels: ["inapp"], // will auto-ensure 'push' if enabled
  };

  const [lowStock, setLowStock] = useState<LowStockValue>(emptyLowStock);

  const getDefaultBusinessName = useCallback(() => {
    try {
      const lastUsed = localStorage.getItem("inventory_business_name");
      if (lastUsed && lastUsed.trim()) return lastUsed.trim();

      const rawUser = localStorage.getItem("user");
      if (!rawUser) return "";
      const user = JSON.parse(rawUser);
      const value =
        user?.businessName ??
        user?.business_name ??
        user?.companyName ??
        user?.name ??
        "";
      return typeof value === "string" ? value.trim() : "";
    } catch {
      return "";
    }
  }, []);

  useEffect(() => {
    if (open) {
      const defaultBusinessName = getDefaultBusinessName();
      setFormData({
        ...emptyForm,
        ...initialData,
        sku: (initialData as any)?.sku || "",
        businessName:
          initialData?.businessName?.trim() || defaultBusinessName || "",
        brand: initialData?.brand?.trim() || "",
      });
      setCategoryInput("");
      setSkuAutoFillLocked(false);
      setAutoGeneratedSku("");
      setSubmitAttempted(false);
      listExpenseCategories("inventory")
        .then((rows) => setCategories(rows))
        .catch((error) =>
          console.error("Failed to load inventory categories:", error),
        );
      getAllInventory()
        .then((response) => setInventoryItems(response.data.items || []))
        .catch((error) =>
          console.error(
            "Failed to load inventory items for SKU generation:",
            error,
          ),
        );
      // hydrate lowStock if initialData has it
      const ld = (initialData as any)?.lowStock || {};
      setLowStock({
        enabled: !!ld.enabled,
        threshold: String(ld.threshold ?? ""),
        alertOnce: ld.alertOnce !== false,
        cooldownHours: String(ld.cooldownHours ?? "24"),
        channels:
          Array.isArray(ld.channels) && ld.channels.length
            ? ld.channels
            : ["inapp"],
      });
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [open, initialData, emptyForm, getDefaultBusinessName]);

  useEffect(() => {
    if (!open || !formData.categoryId || categoryInput) return;
    const matched = categories.find((c) => c._id === formData.categoryId);
    if (matched) setCategoryInput(matched.name);
  }, [open, formData.categoryId, categoryInput, categories]);

  useEffect(() => {
    const categoryName = categoryInput.trim();
    const currentSku = String(formData.sku || "").trim();
    if (!open || !categoryName || skuAutoFillLocked) return;
    const nextSku = nextSkuForCategory(categoryName, inventoryItems);
    const canReplaceCurrentSku =
      currentSku === "" || currentSku === autoGeneratedSku;
    if (!nextSku || !canReplaceCurrentSku || nextSku === currentSku) return;
    setFormData((prev) => ({ ...prev, sku: nextSku }));
    setAutoGeneratedSku(nextSku);
  }, [
    open,
    categoryInput,
    formData.sku,
    inventoryItems,
    nextSkuForCategory,
    skuAutoFillLocked,
    autoGeneratedSku,
  ]);

  const brandOptions = useMemo(
    () =>
      Array.from(
        new Set(
          inventoryItems
            .map((item) => String(item.brand || "").trim())
            .filter(Boolean),
        ),
      ).sort((a, b) => a.localeCompare(b)),
    [inventoryItems],
  );

  const handleClose = () => {
    setFormData(emptyForm);
    setCategoryInput("");
    setSkuAutoFillLocked(false);
    setAutoGeneratedSku("");
    setSubmitAttempted(false);
    setLowStock(emptyLowStock);
    onClose();
  };

  const handleChange = (key: string, value: any) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  };

  const handleAddVariant = () => {
    setFormData((prev) => ({
      ...prev,
      variants: [...prev.variants, { attribute: "", value: "" }],
    }));
  };

  const handleVariantChange = (
    index: number,
    key: "attribute" | "value",
    value: string,
  ) => {
    const newVariants = [...formData.variants];
    newVariants[index][key] = value;
    setFormData((prev) => ({ ...prev, variants: newVariants }));
  };

  const handleRemoveVariant = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewURL = URL.createObjectURL(file);
      setFormData((prev) => ({
        ...prev,
        imageUrl: previewURL,
        imageFile: file, // ✅ Store the actual File
      }));
    }
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    const hasMissingRequiredField =
      !formData.itemName.trim() ||
      !formData.brand.trim() ||
      !formData.businessName.trim() ||
      !formData.quantity.trim() ||
      !formData.costPerUnit.trim() ||
      !formData.defaultSellingPrice.trim() ||
      !categoryInput.trim();
    if (hasMissingRequiredField) return;

    try {
      const quantity = parseFloat(formData.quantity);
      const costPerUnit = parseFloat(formData.costPerUnit);
      const defaultSellingPrice = parseFloat(formData.defaultSellingPrice);
      const typedCategory = categoryInput.trim();
      let resolvedCategoryId = formData.categoryId;

      if (!resolvedCategoryId) {
        if (!typedCategory) {
          throw new Error("Category is required.");
        }
        const existing = categories.find(
          (c) => c.name.trim().toLowerCase() === typedCategory.toLowerCase(),
        );
        if (existing) {
          resolvedCategoryId = existing._id;
        } else {
          const created = await createExpenseCategory(
            typedCategory,
            "inventory",
          );
          resolvedCategoryId = created._id;
          setCategories((prev) =>
            [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
          );
        }
      }
      if (
        !Number.isFinite(quantity) ||
        !Number.isFinite(costPerUnit) ||
        !Number.isFinite(defaultSellingPrice)
      ) {
        throw new Error("Quantity, cost, and selling price must be numbers.");
      }
      setSaving(true);
      const submittedSku = String(formData.sku || "").trim();
      const shouldDeferSkuToBackend =
        submittedSku === "" ||
        (!skuAutoFillLocked && submittedSku === autoGeneratedSku);

      const payload = {
        ...formData,
        categoryId: resolvedCategoryId,
        quantity,
        costPerUnit,
        defaultSellingPrice,
        sku: shouldDeferSkuToBackend ? "" : submittedSku,
        imageFile: formData.imageFile,
        notes: formData.notes || "",
        lowStock: {
          enabled: !!lowStock.enabled,
          threshold: Number(lowStock.threshold || 0),
          alertOnce: !!lowStock.alertOnce,
          channels: lowStock.channels,
          cooldownHours: Math.max(1, Number(lowStock.cooldownHours || 24)),
        },
      };
      if (formData.businessName?.trim()) {
        try {
          localStorage.setItem(
            "inventory_business_name",
            formData.businessName.trim(),
          );
        } catch {
          // ignore storage failures
        }
      }
      await onSave(payload);
      handleClose();
    } catch (error) {
      console.error("Save failed:", error);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [open, initialData, emptyForm]);

  const showItemNameError = submitAttempted && !formData.itemName.trim();
  const showBrandError = submitAttempted && !formData.brand.trim();
  const showBusinessNameError =
    submitAttempted && !formData.businessName.trim();
  const showQuantityError = submitAttempted && !formData.quantity.trim();
  const showCostPerUnitError = submitAttempted && !formData.costPerUnit.trim();
  const showSellingPriceError =
    submitAttempted && !formData.defaultSellingPrice.trim();
  const showCategoryError = submitAttempted && !categoryInput.trim();
  const placeholderInitial =
    formData.itemName.trim().charAt(0) ||
    formData.brand.trim().charAt(0) ||
    "?";

  return (
    <Backdrop
      onClick={handleClose}
      style={{
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? "visible" : "hidden",
      }}
    >
      <MotionBox
        position="relative"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: isVisible ? "translateY(0)" : "translateY(-50%)",
          opacity: isVisible ? 1 : 0,
        }}
      >
        {/* <CloseButton onClick={onClose}>
          <CloseIcon />
        </CloseButton> */}
        <ModalWrapper>
          <LeftPanel>
            <ImageContainer
              htmlFor="imageUpload"
              isDragging={isDragging}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith("image/")) {
                  const previewURL = URL.createObjectURL(file);
                  setFormData((prev) => ({
                    ...prev,
                    imageUrl: previewURL,
                    imageFile: file, // ✅ Store File for upload
                  }));
                }
              }}
            >
              {formData.imageUrl ? (
                <img src={formData.imageUrl} alt="Preview" />
              ) : (
                <div className="placeholder-initial">{placeholderInitial}</div>
              )}
              <span>Click or drag to change image</span>
            </ImageContainer>

            <HiddenInput
              id="imageUpload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
            />
          </LeftPanel>

          <RightPanel>
            <Typography variant="h5" fontWeight="bold" mb={2}>
              Add Inventory Item
            </Typography>

            <FieldRow>
              <Field
                label="Item Name"
                name="itemName"
                value={formData.itemName}
                onChange={(e) => handleChange("itemName", e.target.value)}
                error={showItemNameError}
              />
              <Autocomplete
                fullWidth
                freeSolo
                options={brandOptions}
                value={formData.brand || ""}
                inputValue={formData.brand || ""}
                onInputChange={(_, newInput) => handleChange("brand", newInput)}
                onChange={(_, newValue) =>
                  handleChange(
                    "brand",
                    typeof newValue === "string" ? newValue : "",
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Brand Name"
                    placeholder="Pick saved brand or type a new one"
                    error={showBrandError}
                    sx={compactTextFieldSx}
                  />
                )}
              />
            </FieldRow>

            <FieldRow>
              <Field
                label="Business Name"
                name="businessName"
                value={formData.businessName}
                onChange={(e) => handleChange("businessName", e.target.value)}
                error={showBusinessNameError}
              />
            </FieldRow>

            <FieldRow>
              <Field
                label="Quantity"
                name="quantity"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={formData.quantity}
                onChange={(e) => handleChange("quantity", e.target.value)}
                error={showQuantityError}
              />
              <Field
                label="Cost Per Unit"
                name="costPerUnit"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={formData.costPerUnit}
                onChange={(e) => handleChange("costPerUnit", e.target.value)}
                error={showCostPerUnitError}
              />
            </FieldRow>

            <FieldRow>
              <Field
                label="Selling Price"
                name="defaultSellingPrice"
                type="text"
                inputProps={{ inputMode: "decimal" }}
                value={formData.defaultSellingPrice}
                onChange={(e) =>
                  handleChange("defaultSellingPrice", e.target.value)
                }
                error={showSellingPriceError}
              />
            </FieldRow>
            <FieldRow>
              <Box flex={2}>
                <Autocomplete
                  fullWidth
                  freeSolo
                  options={categories}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : option.name
                  }
                  value={
                    categories.find((cat) => cat._id === formData.categoryId) ||
                    null
                  }
                  inputValue={categoryInput}
                  onInputChange={(_, newInput, reason) => {
                    setCategoryInput(newInput);
                    setSkuAutoFillLocked(false);
                    if (reason === "input") handleChange("categoryId", "");
                  }}
                  onChange={(_, newValue) => {
                    if (newValue && typeof newValue === "object") {
                      setCategoryInput(newValue.name);
                      setSkuAutoFillLocked(false);
                      handleChange("categoryId", newValue._id);
                      return;
                    }
                    if (typeof newValue === "string") {
                      setCategoryInput(newValue);
                      setSkuAutoFillLocked(false);
                    }
                    handleChange("categoryId", "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Category"
                      placeholder="Type to filter or create"
                      error={showCategoryError}
                      sx={compactTextFieldSx}
                    />
                  )}
                />
              </Box>
              <Box flex={1}>
                <Field
                  label="SKU"
                  name="sku"
                  value={(formData as any).sku || ""}
                  onChange={(e) => {
                    setSkuAutoFillLocked(true);
                    setAutoGeneratedSku("");
                    handleChange("sku", e.target.value);
                  }}
                />
              </Box>
            </FieldRow>
            {!categories.length && (
              <Typography variant="caption" color="textSecondary">
                Start typing a category; it will be created on save if missing.
              </Typography>
            )}
            <Typography variant="caption" color="textSecondary" sx={{ mb: 2 }}>
              If SKU is left empty, one will be auto-assigned based on the
              selected category.
            </Typography>
            {/* Notes */}
            <TextField
              label="Notes"
              value={formData.notes || ""}
              onChange={(e) => handleChange("notes", e.target.value)}
              multiline
              minRows={4}
              fullWidth
              sx={{ mb: 2 }}
            />
            {formData.variants.map((variant, index) => (
              <VariantRow key={index}>
                <Field
                  label="Attribute"
                  placeholder="Attribute"
                  value={variant.attribute}
                  onChange={(e) =>
                    handleVariantChange(index, "attribute", e.target.value)
                  }
                />
                <Field
                  label="Value"
                  placeholder="Value"
                  value={variant.value}
                  onChange={(e) =>
                    handleVariantChange(index, "value", e.target.value)
                  }
                />
                <RemoveButton
                  onClick={() => handleRemoveVariant(index)}
                  size="small"
                >
                  <DeleteIcon fontSize="small" />
                </RemoveButton>
              </VariantRow>
            ))}

            <AddVariantButton onClick={handleAddVariant}>
              + Add Variant
            </AddVariantButton>
            {/* Low Stock Alert */}
            <LowStockControlsWeb value={lowStock} onChange={setLowStock} />
            <Box mt={3} mb={2} display="flex" justifyContent="flex-end">
              {saving ? (
                <Button
                  variant="contained"
                  disabled
                  style={{
                    backgroundColor: "transparent",
                    height: "36px",
                    minWidth: "100px",
                  }}
                >
                  <DotLoader />
                </Button>
              ) : (
                <>
                  <Button
                    onClick={onClose}
                    variant="outlined"
                    style={{
                      borderColor: "#2563eb",
                      color: "#2563eb",
                      height: "36px",
                      minWidth: "100px",
                      marginRight: "1rem",
                    }}
                  >
                    Cancel
                  </Button>

                  <Button
                    onClick={handleSave}
                    variant="contained"
                    style={{
                      backgroundColor: "#2563eb",
                      height: "36px",
                      minWidth: "100px",
                    }}
                  >
                    Save
                  </Button>
                </>
              )}
            </Box>
          </RightPanel>
        </ModalWrapper>
      </MotionBox>
    </Backdrop>
  );
};

export default AddInventoryModal;
